import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Archive,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Eye,
  ArrowLeft,
} from 'lucide-react';

const StudentChallengesArchive = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [challenges, setChallenges] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const loadArchive = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await api.get(`/challenges/my/archive?page=${page}&limit=10`);
        if (res.data?.success && res.data.data) {
          const { challenges: list, pagination: pag } = res.data.data;
          setChallenges(list || []);
          setPagination({
            page: pag?.page ?? 1,
            limit: pag?.limit ?? 10,
            total: pag?.total ?? 0,
            pages: pag?.pages ?? 1,
          });
        }
      } catch (e) {
        console.error(e);
        toast.error(t('studentChallenges.archiveError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadArchive(1);
  }, [loadArchive]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const truncate = (s, n = 48) => {
    if (!s || typeof s !== 'string') return '—';
    return s.length <= n ? s : `${s.slice(0, n)}…`;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <Link
        to="/student/challenges"
        className={`inline-flex items-center gap-2 text-sm font-bold text-violet-800 hover:text-violet-950 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
      >
        <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} aria-hidden />
        {t('studentChallenges.backToAvailable')}
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border-[3px] border-slate-900/10 bg-gradient-to-br from-slate-600 to-slate-800 text-white shadow-md">
          <Archive className="h-6 w-6" strokeWidth={2.25} aria-hidden />
        </div>
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">{t('studentChallenges.archiveTitle')}</h1>
          <p className="text-sm font-medium text-slate-600 mt-0.5">{t('studentChallenges.archiveSubtitle')}</p>
        </div>
      </div>

      <div className="rounded-2xl border-[3px] border-slate-900/10 bg-white/90 shadow-[0_6px_0_0_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200 bg-slate-50/90">
                <th className={`py-3 px-3 font-bold text-slate-800 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('studentChallenges.colName')}
                </th>
                <th className="py-3 px-3 font-bold text-slate-800 text-center whitespace-nowrap">
                  {t('studentChallenges.colPeriod')}
                </th>
                <th className="py-3 px-3 font-bold text-slate-800 text-center whitespace-nowrap">
                  {t('studentChallenges.colStatus')}
                </th>
                <th className="py-3 px-3 font-bold text-slate-800 text-center whitespace-nowrap">
                  {t('studentChallenges.colResult')}
                </th>
                <th className={`py-3 px-3 font-bold text-slate-800 max-w-[12rem] ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('studentChallenges.colMyAnswer')}
                </th>
                <th className="py-3 px-3 font-bold text-slate-800 text-center whitespace-nowrap">
                  {t('studentChallenges.colSubmittedAt')}
                </th>
                <th className="py-3 px-3 font-bold text-slate-800 text-center whitespace-nowrap">
                  {t('studentChallenges.colAction')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-violet-600 mx-auto" aria-hidden />
                  </td>
                </tr>
              ) : challenges.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-600 font-medium">
                    {t('studentChallenges.archiveEmpty')}
                  </td>
                </tr>
              ) : (
                challenges.map((ch) => {
                  const sub = ch.mySubmission;
                  const hasSub = sub && typeof sub === 'object';
                  return (
                    <tr key={ch.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                      <td className={`py-3 px-3 font-semibold text-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {ch.name}
                      </td>
                      <td className="py-3 px-3 text-center text-slate-600 text-xs whitespace-nowrap">
                        {formatDate(ch.startsAt)} → {formatDate(ch.endsAt)}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="inline-flex rounded-full bg-slate-200/90 px-2 py-0.5 text-xs font-bold text-slate-800 capitalize">
                          {ch.status || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {!hasSub ? (
                          <span className="text-slate-400 text-xs">—</span>
                        ) : sub.isCorrect ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                            <CheckCircle2 className="h-5 w-5" aria-hidden />
                            {t('studentChallenges.correct')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-700 font-bold">
                            <XCircle className="h-5 w-5" aria-hidden />
                            {t('studentChallenges.incorrect')}
                          </span>
                        )}
                      </td>
                      <td className={`py-3 px-3 text-slate-700 text-xs max-w-[14rem] ${isRTL ? 'text-right' : 'text-left'}`}>
                        {hasSub && sub.answerValue != null ? truncate(String(sub.answerValue)) : '—'}
                      </td>
                      <td className="py-3 px-3 text-center text-slate-600 text-xs whitespace-nowrap">
                        {hasSub && sub.submittedAt ? formatDate(sub.submittedAt) : '—'}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <Link
                          to={`/student/challenges/${ch.id}`}
                          className="inline-flex items-center gap-1.5 rounded-xl border-2 border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-900 hover:bg-violet-100 transition-colors"
                        >
                          <Eye className="h-4 w-4 shrink-0" aria-hidden />
                          {t('studentChallenges.btnView')}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && pagination.pages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50/80">
            <p className="text-xs font-medium text-slate-600">
              {t('studentChallenges.pageOf', {
                page: pagination.page,
                pages: pagination.pages,
                total: pagination.total,
              })}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => loadArchive(pagination.page - 1)}
                className="inline-flex items-center gap-1 rounded-xl border-2 border-slate-900/15 bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40"
              >
                {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                {t('common.previous')}
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.pages}
                onClick={() => loadArchive(pagination.page + 1)}
                className="inline-flex items-center gap-1 rounded-xl border-2 border-slate-900/15 bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40"
              >
                {t('common.next')}
                {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentChallengesArchive;
